import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { captureAttribution, getAttribution } from "@/lib/attribution";

type Field = { name: string; label: string; type: string; required?: boolean };

export default function LandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [promo, setPromo] = useState<any>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("landing_pages").select("*").eq("slug", slug).eq("published", true).maybeSingle();
      if (!data) { setNotFound(true); setLoading(false); return; }
      setPage(data);
      if (data.offer_promo_code_id) {
        const { data: p } = await supabase.rpc("get_public_promo" as any, { _id: data.offer_promo_code_id });
        setPromo(Array.isArray(p) ? p[0] : p);
      }
      // SEO
      document.title = data.seo_title || data.title || "MyStudio";
      const m = document.querySelector('meta[name="description"]') || (() => {
        const x = document.createElement("meta"); x.setAttribute("name", "description"); document.head.appendChild(x); return x;
      })();
      m.setAttribute("content", data.seo_description || data.subheadline || "");
      setLoading(false);
      // Capture UTM + log a landing_page_view (best-effort)
      try {
        const attr = captureAttribution({ slug });
        await supabase.rpc("track_landing_view" as any, {
          _slug: slug, _attribution: attr,
          _referrer: document.referrer || null,
          _user_agent: navigator.userAgent.slice(0, 500),
          _session_id: attr.session_id ?? null,
        });
      } catch { /* ignore */ }
    })();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (notFound) return <Navigate to="/404" replace />;

  const fields: Field[] = page.form_fields ?? [];

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    for (const f of fields) if (f.required && !values[f.name]?.trim()) return toast.error(`${f.label} required`);
    setSubmitting(true);
    const attribution = getAttribution();
    const { error: subErr } = await supabase.from("landing_page_submissions").insert({
      landing_page_id: page.id, data: values, attribution,
    } as any);
    if (subErr) { setSubmitting(false); return toast.error(subErr.message); }
    // Capture as lead (RPC, public-exec)
    if (values.email) {
      const { data: leadId } = await supabase.rpc("capture_lead", {
        _full_name: values.full_name ?? null, _email: values.email,
        _phone: values.phone ?? null, _source: "landing_page", _source_ref: page.slug,
        _tags: [`lp:${page.slug}`], _custom: values, _notes: null,
      });
      if (leadId) {
        await supabase.rpc("set_lead_attribution" as any, { _lead_id: leadId, _attribution: attribution });
      }
    }
    setSubmitting(false); setDone(true);
  }

  return (
    <main className="min-h-screen bg-background text-foreground">
      {page.hero_image && (
        <div className="w-full aspect-[21/9] bg-panel overflow-hidden">
          <img src={page.hero_image} alt={page.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="container mx-auto max-w-3xl px-4 py-16">
        <h1 className="text-4xl md:text-6xl font-display tracking-tight">{page.headline || page.title}</h1>
        {page.subheadline && <p className="mt-4 text-xl text-muted-foreground">{page.subheadline}</p>}
        {promo && (
          <div className="mt-6 inline-block bg-primary/10 border border-primary text-primary px-4 py-2 font-mono text-sm">
            CODE: <strong>{promo.code}</strong> — {promo.discount_type === "percent" ? `${promo.discount_value}% off` : `$${promo.discount_value} off`}
            {promo.description && <span className="ml-2 opacity-80">· {promo.description}</span>}
          </div>
        )}
        {page.body_md && <div className="mt-8 prose prose-invert max-w-none whitespace-pre-wrap">{page.body_md}</div>}

        <div className="mt-10 max-w-md">
          {done ? (
            <div className="bg-surface border border-border p-6 flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
              <div>
                <h3 className="font-display text-lg">Thanks!</h3>
                <p className="text-muted-foreground mt-1">{page.success_message || "We'll be in touch shortly."}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-surface border border-border p-6 space-y-3">
              {fields.map(f => (
                <div key={f.name}>
                  <Label>{f.label}{f.required ? " *" : ""}</Label>
                  <Input type={f.type} value={values[f.name] ?? ""} onChange={e => setValues({ ...values, [f.name]: e.target.value })} required={f.required} />
                </div>
              ))}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (page.cta_label || "Submit")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}